import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, Search, UnfoldMore } from '@styled-icons/material';
import { CURRENCY_LIST, CURRENCY_SYMBOLS, Currency, CurrencyDecimal } from '@typedream/data';
import { Text, cn } from '@typedream/ui';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import sharedStyles from '../../styles/Shared.module.css';
import style from './style.module.scss';
import { PriceInputProps } from './type';

const sortedCurrencyList = Object.values(CURRENCY_LIST).sort((a, b) =>
  a.code.localeCompare(b.code)
);

export function PriceInput({
  initialAmount = '',
  initialCurrency = Currency.USD,
  error = false,
  onUpdate = () => null,
}: PriceInputProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [amount, setAmount] = useState(initialAmount);
  const [currency, setCurrency] = useState<Currency>(initialCurrency);
  const [specialCase, setSpecialCase] = useState<CurrencyDecimal | undefined>();
  const [search, setSearch] = useState('');

  const filteredList = useMemo(
    () =>
      sortedCurrencyList.filter(
        (item) =>
          item.code.toLowerCase().includes(search.toLowerCase()) ||
          item.country.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  );

  return (
    <div
      className={classNames(sharedStyles.inputMedium, style.inputWrapper, {
        [sharedStyles.inputError]: error,
      })}
      style={{ minWidth: 100 }}
    >
      <Text size="sm" weight="medium" variant="mute" className={style.symbol}>
        {CURRENCY_SYMBOLS[currency]}
      </Text>
      <input
        className={classNames(sharedStyles.inputWithPrefix, style.input)}
        type="text"
        value={amount}
        placeholder="5.00"
        onChange={(e) => {
          let pattern = /^(\d{0,})(?:\.\d{0,2})?$/;

          if (currency === Currency.HUF) {
            pattern = /^(\d{0,})$/;
          }

          if (pattern.test(e.target.value)) {
            onUpdate({ amount: e.target.value, currency, specialCase });
            setAmount(e.target.value);
          }
        }}
      />
      <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenu.Trigger className={style.currencySelect}>
          <Text size="sm" weight="medium" variant="mute" className={style.currencySelectText}>
            {currency.toUpperCase()}
          </Text>
          <UnfoldMore size={16} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content asChild sideOffset={12} align="start" style={{ zIndex: 20 }}>
            <div
              className={cn(
                'flex flex-col w-[322px] border border-border border-solid bg-background',
                'rounded-md shadow-xl overflow-hidden max-h-[293px]'
              )}
            >
              <div className={style.searchContent}>
                <div className={cn(style.searchWrapper, 'bg-background-tertiary')}>
                  <Search size={20} color="var(--deprecated-neutral-600)" />
                  <input
                    type="text"
                    placeholder="Search currency"
                    className={style.searchInput}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value.trim());
                    }}
                  />
                </div>
              </div>
              <div className={style.currencyList}>
                {filteredList.map(({ code, country, decimal }) => (
                  <button
                    type="button"
                    key={code}
                    className={classNames(style.currencyItem, {
                      [style.active]: code === currency,
                    })}
                    onClick={() => {
                      onUpdate({ amount, currency: code, specialCase: decimal });
                      setCurrency(code);
                      setDropdownOpen(false);
                      setSpecialCase(decimal);
                    }}
                  >
                    <Text variant="main" size="xs" weight="medium">
                      {code.toUpperCase()} - {country}
                    </Text>
                    {code === currency ? <Check size={20} color="#98A2B3" /> : null}
                  </button>
                ))}
              </div>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
